import { useCallback } from "react";
import { useQueryParams } from "./use-query-params";

/**
 * Hook for managing modal state via URL query parameters.
 * Enables browser back button to close modals and shareable modal states.
 *
 * @param modalKey - Unique identifier for this modal (e.g., 'edit', 'delete', 'import')
 * @returns [isOpen, setOpen] - Tuple of current state and setter function
 *
 * @example
 * ```tsx
 * const [dialogOpen, setDialogOpen] = useUrlModal('edit');
 *
 * return (
 *   <>
 *     <Button onClick={() => setDialogOpen(true)}>Open</Button>
 *     <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
 *       Content
 *     </Dialog>
 *   </>
 * );
 * ```
 */
export function useUrlModal(modalKey: string): [boolean, (open: boolean) => void] {
  const { params, setParam } = useQueryParams();

  // Check if this modal is currently open
  const isOpen = params.modal === modalKey;

  // Setter function to open/close the modal
  const setOpen = useCallback(
    (open: boolean) => {
      if (open) {
        // Open modal by setting the modal param to this key
        setParam("modal", modalKey);
      } else {
        // Close modal by removing the modal param
        setParam("modal", undefined);
      }
    },
    [modalKey, setParam],
  );

  return [isOpen, setOpen];
}
